import { useState } from "react";
import { DSE_CATEGORY_LABEL, DSE_INSTRUMENT_VERSION, DSE_ITEMS, WORK_LOCATIONS } from "@backcoach/domain";
import type { WorkLocation } from "@backcoach/domain";
import { useApi, unwrap } from "../client.js";
import type { WorkstationView } from "../hooks.js";
import Questionnaire from "./Questionnaire.js";
import type { Question } from "./Questionnaire.js";

/**
 * The DSE check: the work location first, then 24 questions across the six
 * HSE categories, one per screen. Marketed as compliance and nothing else,
 * which is why no question asks about pain and no result claims an effect
 * on it.
 */
const QUESTIONS: Question[] = [
  {
    id: "work_location",
    group: "Where you work",
    text: "Where do you do most of your screen work?",
    options: WORK_LOCATIONS.map((l) => ({ value: l.value, label: l.label })),
  },
  ...DSE_ITEMS.map((item): Question => ({
    id: item.id,
    group: DSE_CATEGORY_LABEL[item.category],
    text: item.prompt,
    options: item.options.map((o) => ({ value: o.value, label: o.label })),
    ...(item.skipWhen ? { skipIf: (a: Record<string, string>) => a[item.skipWhen?.item ?? ""] === item.skipWhen?.value } : {}),
  })),
];

export default function DseFlow({ onDone, onExit }: { onDone: (view: WorkstationView) => void; onExit: () => void }) {
  const api = useApi();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      {error && <p className="bc-note">{error}</p>}
      <Questionnaire
        title="Workstation check"
        questions={QUESTIONS}
        onExit={onExit}
        onComplete={(a) => {
          const { work_location, ...answers } = a;
          void (async () => {
            try {
              const r = unwrap(
                await api.POST("/v1/me/dse", {
                  body: { workLocation: (work_location ?? "office") as WorkLocation, answers, instrumentVersion: DSE_INSTRUMENT_VERSION },
                }),
              );
              onDone(r);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Could not record that");
            }
          })();
        }}
      />
    </>
  );
}
